/**
 * ScopeSelector — "Who is this problem affecting?" step
 *
 * Presented as the first question when creating a new workspace.
 * The user's selection configures:
 *   - scope_type on the workspace
 *   - which phases are required vs. optional
 *   - whether a community assignment is needed
 *
 * Three options, presented as clear cards:
 *   personal  → "Just me"
 *   household → "My household or building"
 *   community → "My community"
 */

import React from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { WorkspaceScopeType } from "../../api/workspaces";
import styles from "./ScopeSelector.module.css";

interface Props {
  value: WorkspaceScopeType | null;
  onChange: (scope: WorkspaceScopeType) => void;
}

const SCOPE_OPTIONS: Array<{
  key: WorkspaceScopeType;
  icon: string;
  titleKey: string;
  descKey: string;
  phasesKey: string;
}> = [
  {
    key: "personal",
    icon: "👤",
    titleKey: "workspace.scope.personalTitle",
    descKey: "workspace.scope.personalDesc",
    phasesKey: "workspace.scope.personalPhases",
  },
  {
    key: "household",
    icon: "🏠",
    titleKey: "workspace.scope.householdTitle",
    descKey: "workspace.scope.householdDesc",
    phasesKey: "workspace.scope.householdPhases",
  },
  {
    key: "community",
    icon: "🏘️",
    titleKey: "workspace.scope.communityTitle",
    descKey: "workspace.scope.communityDesc",
    phasesKey: "workspace.scope.communityPhases",
  },
];

const ScopeSelector: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useAppTranslation("pages");

  return (
    <div className={styles.container}>
      <h3 className={styles.question}>
        {t("workspace.scope.question")}
      </h3>
      <p className={styles.hint}>{t("workspace.scope.hint")}</p>

      <div className={styles.options}>
        {SCOPE_OPTIONS.map((opt) => {
          const selected = value === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              className={`${styles.card} ${selected ? styles.cardSelected : ""}`}
              onClick={() => onChange(opt.key)}
              aria-pressed={selected}
            >
              <span className={styles.icon}>{opt.icon}</span>
              <span className={styles.title}>{t(opt.titleKey)}</span>
              <span className={styles.desc}>{t(opt.descKey)}</span>
              <span className={styles.phases}>{t(opt.phasesKey)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ScopeSelector;
